import styled, { css } from 'styled-components';

import { getPlanCardStyle } from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.helpers';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CONTAINER_MAX } from '@/constants/breakpoints';
import { flexBetween, flexColumn } from '@/styles/common';

export const ModalSection = styled.div<{
  $isExtraContentVisible: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const ModalContentBox = styled.div`
  ${flexColumn};
  height: 100%;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    align-items: center;
    gap: 10px;
  }
`;

export const PlanSelectionCard = styled.div`
  border-radius: 28px;
  padding: 30px;
  width: 100%;
  max-width: 520px;
  min-height: 205px;
  background: var(--bg-main);
  ${flexColumn};
  gap: 20px;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    max-width: 100%;
    padding: 25px 40px 30px 40px;
    border: 1.5px solid var(--border-secondary);
  }

  @media (max-width: ${CONTAINER_MAX.mobileL}) {
    padding-inline: 20px;
    justify-content: space-between;
  }
`;

export const PlanTitle = styled(Typography).attrs({
  variant: 'title4'
})`
  color: var(--textAndLabels-main);
`;

export const PlanOptionsRow = styled.div`
  ${flexBetween};
  gap: 10px;
`;

export const PlanOptionButton = styled.button<{
  $isActiveCard: boolean;
}>`
  list-style-type: none;
  width: 100%;
  height: 100%;
  min-height: 107px;
  border-radius: 21px;
  ${flexColumn};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  background: ${({ $isActiveCard }) =>
    $isActiveCard ? 'var(--system-lightBlue)' : 'var(--bg-secondary)'};

  border: 2px solid
    ${({ $isActiveCard }) =>
      $isActiveCard ? 'var(--textAndLabels-main)' : 'var(--border-secondary)'};

  ${({ $isActiveCard }) =>
    !$isActiveCard &&
    css`
      transition:
        background 180ms ease,
        border-color 180ms ease,
        box-shadow 180ms ease;
    `}

  ${({ $isActiveCard }) =>
    $isActiveCard &&
    css`
      transition: none;

      &:hover {
        background: var(--system-lightBlue);
        border-color: var(--textAndLabels-main);
        box-shadow: none;
      }
    `}

  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: background, border-color, box-shadow;
  -webkit-tap-highlight-color: transparent;

  outline: none;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--focus);
  }
`;

export const PlanOptionContent = styled.div`
  ${flexColumn};
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

export const PlanDuration = styled(Typography).attrs<{
  $isActiveCard: boolean;
  $isDark: boolean;
}>({
  variant: 'title4'
})`
  color: ${({ $isActiveCard, $isDark }) =>
    getPlanCardStyle($isActiveCard, $isDark).color};
  padding-block-end: 6.5px;
`;

export const PlanPeriodLabel = styled(Typography).attrs({
  variant: 'body5'
})`
  color: var(--textAndLabels-tertiary);
  padding-block-end: 6.5px;
  width: 100%;
  text-align: center;
  word-break: break-word;
  white-space: normal;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    max-width: 46px;
  }
`;

export const PlanPrice = styled(Typography).attrs<{
  $isActiveCard: boolean;
  $isDark: boolean;
}>({
  variant: 'h4'
})`
  color: ${({ $isActiveCard, $isDark }) =>
    getPlanCardStyle($isActiveCard, $isDark).color};
`;

export const PaymentMethodSection = styled.div`
  width: 100%;
  height: 100%;
`;

export const PaymentMethodCard = styled.div`
  border-radius: 28px;
  padding: 30px;
  width: 100%;
  max-width: 520px;
  background: var(--bg-main);
  ${flexColumn};
  gap: 20px;
  margin-block-end: 5px;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    margin-block-end: 0;
    max-width: 100%;
    padding: 30px 40px;
    border: 1.5px solid var(--border-secondary);
    min-height: auto;
  }

  @media (max-width: ${CONTAINER_MAX.mobileL}) {
    padding-inline: 20px;
  }
`;

export const PaymentMethodHeader = styled.div`
  ${flexBetween};
`;

export const PaymentMethodTitle = styled(Typography).attrs({
  variant: 'title4'
})`
  color: var(--textAndLabels-main);
`;

export const PaymentCheckboxList = styled.div`
  ${flexColumn};
  gap: 4px;
`;

export const MobilePaymentLabelGroup = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
`;

export const MobilePaymentSeparator = styled.span`
  width: 1px;
  height: 15px;
  background: var(--border-primary);
  margin-inline-end: 10px;
`;
